/**
 * useAnimationFrame — shared, reduced-motion-aware RAF loop.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useEffect } from "react";
import { useReducedMotion } from "./useReducedMotion.js";
import { useStableCallback } from "./useStableCallback.js";

export interface AnimationFrameState {
  readonly now: number;
  readonly deltaMs: number;
  readonly elapsedMs: number;
}

export interface UseAnimationFrameOptions {
  readonly enabled?: boolean;
  readonly maxFps?: number;
  readonly respectReducedMotion?: boolean;
}

export function useAnimationFrame(
  onFrame: (state: AnimationFrameState) => void,
  options: UseAnimationFrameOptions = {},
): void {
  const { enabled = true, maxFps, respectReducedMotion = true } = options;
  const reducedMotion = useReducedMotion();
  const stableOnFrame = useStableCallback(onFrame);

  useEffect(() => {
    if (!enabled) return undefined;
    if (respectReducedMotion && reducedMotion) return undefined;
    if (typeof requestAnimationFrame !== "function") return undefined;

    const minDeltaMs = maxFps === undefined ? 0 : 1000 / maxFps;
    let raf = 0;
    let start = 0;
    let previous = 0;
    let lastEmitted = 0;

    const tick = (now: number): void => {
      if (start === 0) {
        start = now;
        previous = now;
        lastEmitted = now;
      }
      if (now - lastEmitted >= minDeltaMs) {
        stableOnFrame({
          now,
          deltaMs: now - previous,
          elapsedMs: now - start,
        });
        previous = now;
        lastEmitted = now;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return (): void => {
      cancelAnimationFrame(raf);
    };
  }, [enabled, maxFps, reducedMotion, respectReducedMotion, stableOnFrame]);
}
